<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { GeneralInformation } from 'shared/src'
import { useValidations } from '@/composables/api/useValidations'
import { useFeatureFlags } from '@/composables/env/useFeatureFlags'
import { useAvailableVoices } from '@/composables/configurator/useAvailableVoices'
import {
  useVirtualAgentMainSkills,
  type MainSkills,
} from '@/composables/configurator/useVirtualAgentMainSkills'
import type { VirtualAgentRagSettings } from '@/types/virtualAgentRagSettings'
import AdvancedRagSettingsModal from '@/components/BusinessLogic/Modal/AdvancedRagSettingsModal.vue'
import { useLocalStorage } from '@vueuse/core'
import { capitalize, isNotBlank } from '@/utils/String.ts'
import { WebComponentTags } from '@/enums/WebComponents.ts'
import { useAnalytics, toToggleStatus } from '@/composables/analytics/useAnalytics'
import { STORAGE_KEYS } from '@/enums/StorageEnum.ts'

const { t, locale } = useI18n()
const { track } = useAnalytics()

const modelValue = defineModel<GeneralInformation>({ required: true })
const mainSkills = defineModel<MainSkills>('mainSkills', { required: true })
const ragSettings = defineModel<VirtualAgentRagSettings>('ragSettings', { required: true })
const { visibleErrors } = useValidations()
const {
  isLoadingVoices,
  loadAvailableVoices,
  getVoiceOptionsForLang,
  getDefaultVoiceForLang,
  getVoiceSamplePath,
} = useAvailableVoices()
const {
  qualifyCallsByIntent,
  answerQuestionsWithDocumentation,
  stepValidation,
  hasMultipleMainSkills,
} = useVirtualAgentMainSkills(mainSkills)
const props = withDefaults(defineProps<{ customSkillsEnabled?: boolean }>(), {
  customSkillsEnabled: false,
})
const emit = defineEmits<{ navigateToExperimental: []; defaultLanguageChanged: [code: string] }>()
const isAdvancedRagSettingsModalOpen = ref(false)
const showOnlySelected = useLocalStorage(STORAGE_KEYS.VA_SHOW_ONLY_SELECTED_LANGUAGE, false)

const displayNames = new Intl.DisplayNames([locale.value], { type: 'language' })

const betaLanguageCodes = new Set(['it-IT', 'pt-PT', 'nl-NL', 'pl-PL', 'sv-SE', 'da-DK', 'ar-SA'])

const allLanguageOptions = [
  { code: 'fr-FR', label: capitalize(displayNames.of('fr') ?? 'fr') },
  { code: 'en-US', label: capitalize(displayNames.of('en') ?? 'en') },
  { code: 'de-DE', label: capitalize(displayNames.of('de') ?? 'de') },
  { code: 'es-ES', label: capitalize(displayNames.of('es') ?? 'es') },
  { code: 'it-IT', label: capitalize(displayNames.of('it') ?? 'it') },
  { code: 'pt-PT', label: capitalize(displayNames.of('pt') ?? 'pt') },
  { code: 'nl-NL', label: capitalize(displayNames.of('nl') ?? 'nl') },
  { code: 'pl-PL', label: capitalize(displayNames.of('pl') ?? 'pl') },
  { code: 'sv-SE', label: capitalize(displayNames.of('sv') ?? 'sv') },
  { code: 'da-DK', label: capitalize(displayNames.of('da') ?? 'da') },
]

const { showArabicFeatures } = useFeatureFlags()

const arabicLanguageOption = { code: 'ar-SA', label: capitalize(displayNames.of('ar') ?? 'ar') }

const languageOptions = computed(() => {
  const base = [...allLanguageOptions]
  const showArabic =
    typeof showArabicFeatures === 'boolean' ? showArabicFeatures : showArabicFeatures.value
  if (showArabic) base.push(arabicLanguageOption)
  return base
})

const enabledLanguageCodes = computed<string[]>(() => {
  const voiceIds = modelValue.value.voice_ids
  if (!voiceIds || Object.keys(voiceIds).length === 0) {
    return [supportedLanguages.value.find((l) => l.default)?.code ?? 'fr-FR']
  }
  const keys = Object.keys(voiceIds)
  return languageOptions.value
    .filter((lang) => keys.includes(normalizeLangCode(lang.code)))
    .map((lang) => lang.code)
})

const normalizeLangCode = (code: string) => code.replace(/[^a-z]/g, '')

const supportedLanguages = computed({
  get: () => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return modelValue.value.supported_languages ?? []
  },
  set: (val) => {
    modelValue.value.supported_languages = val
  },
})

const isLanguageEnabled = (code: string) => enabledLanguageCodes.value.includes(code)

const filteredLanguageOptions = computed(() =>
  showOnlySelected.value
    ? languageOptions.value.filter((l) => isLanguageEnabled(l.code))
    : languageOptions.value,
)
const hasBetaBadge = (code: string) => betaLanguageCodes.has(code)

const setDefaultLanguage = (code: string) => {
  supportedLanguages.value = supportedLanguages.value.map((lang) => ({
    ...lang,
    default: lang.code === code,
  }))
  emit('defaultLanguageChanged', code)
}

const ensureDefaultLanguage = () => {
  const enabled = enabledLanguageCodes.value
  if (enabled.length === 0) return

  const defaultLang = supportedLanguages.value.find(
    (lang) => lang.default && enabled.includes(lang.code),
  )
  if (!defaultLang) {
    setDefaultLanguage(enabled[0])
  }
}

// Load available voices from API
onMounted(async () => {
  await loadAvailableVoices()

  // Ensure full list of available languages is present
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!modelValue.value.supported_languages?.length) {
    supportedLanguages.value = languageOptions.value.map((lang) => ({
      code: lang.code,
      audio: '',
      default: lang.code === 'fr-FR',
    }))
  } else {
    // Backfill missing languages from old configs that only stored a subset
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const existingCodes = new Set(modelValue.value.supported_languages?.map((l) => l.code) ?? [])
    const missingLangs = languageOptions.value
      .filter((lang) => !existingCodes.has(lang.code))
      .map((lang) => ({ code: lang.code, audio: '', default: false }))
    if (missingLangs.length > 0) {
      supportedLanguages.value = [...modelValue.value.supported_languages, ...missingLangs]
    }
  }

  // Initialize voice_ids if not present
  modelValue.value.voice_ids ??= {}

  ensureDefaultLanguage()

  // Set default voices for enabled languages
  enabledLanguageCodes.value.forEach((langCode) => {
    const normalizedCode = langCode.replace(/[^a-z]/g, '')
    if (!modelValue.value.voice_ids?.[normalizedCode]) {
      const defaultVoiceId = getDefaultVoiceForLang(langCode)
      if (defaultVoiceId && modelValue.value.voice_ids) {
        modelValue.value.voice_ids[normalizedCode] = defaultVoiceId
      }
    }
  })
})

const toggleLanguage = (code: string, active: boolean) => {
  track('Language Toggle Changed', {
    status: toToggleStatus(active),
    language: code.split('-')[0],
  })
  const wasDefault = supportedLanguages.value.find((lang) => lang.code === code)?.default ?? false
  const wasEnabled = enabledLanguageCodes.value.includes(code)

  if (active && !wasEnabled) {
    // Set default voice_id for this language
    const langCode = code.replace(/[^a-z]/g, '')
    const defaultVoiceId = getDefaultVoiceForLang(code)
    if (defaultVoiceId && modelValue.value.voice_ids) {
      modelValue.value.voice_ids[langCode] = defaultVoiceId
    }
    ensureDefaultLanguage()
  } else if (!active && wasEnabled) {
    // Don't allow disabling the last language
    if (enabledLanguageCodes.value.length <= 1) return

    // Remove voice_id for this language
    const langCode = code.replace(/[^a-z]/g, '')
    if (modelValue.value.voice_ids) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete modelValue.value.voice_ids[langCode]
    }
    if (wasDefault) {
      setDefaultLanguage(enabledLanguageCodes.value[0])
    }
  }
}

// Create a computed for each language code to use with v-model
const voiceIdByLang = (langCode: string) => {
  return computed({
    get: () => {
      const normalizedCode = langCode.replace(/[^a-z]/g, '')
      const selectedVoiceId = modelValue.value.voice_ids?.[normalizedCode]
      return isNotBlank(selectedVoiceId)
        ? selectedVoiceId
        : (getDefaultVoiceForLang(langCode) ?? '')
    },
    set: (voiceId: string) => {
      const normalizedCode = langCode.replace(/[^a-z]/g, '')
      modelValue.value.voice_ids ??= {}
      modelValue.value.voice_ids[normalizedCode] = voiceId
      if (!isLanguageEnabled(langCode)) {
        toggleLanguage(langCode, true)
      }
    },
  })
}

// Helper to get sample path for the currently selected voice
const getSelectedVoiceSamplePath = (langCode: string): string => {
  const normalizedCode = langCode.replace(/[^a-z]/g, '')
  const selectedVoiceId = modelValue.value.voice_ids?.[normalizedCode]
  const voiceId = isNotBlank(selectedVoiceId)
    ? selectedVoiceId
    : (getDefaultVoiceForLang(langCode) ?? '')
  if (!voiceId) return ''
  return getVoiceSamplePath(langCode, voiceId)
}

const defaultLanguageCode = computed({
  get: () => {
    const found = supportedLanguages.value.find((l) => l.default && isLanguageEnabled(l.code))?.code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return found ?? enabledLanguageCodes.value[0] ?? ''
  },
  set: (newCode: string) => {
    if (!isLanguageEnabled(newCode)) {
      toggleLanguage(newCode, true)
    }
    track('Default Language Changed', {
      status: 'Enabled',
      language: newCode.split('-')[0],
    })
    setDefaultLanguage(newCode)
  },
})

watch(qualifyCallsByIntent, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    track('Qualify Intent Toggle Changed', {
      status: toToggleStatus(newVal),
    })
  }
})

watch(answerQuestionsWithDocumentation, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    track('Answer With Doc Toggle Changed', {
      status: toToggleStatus(newVal),
    })
  }
})

watch(stepValidation, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    track('Step Validation Toggle Changed', {
      status: toToggleStatus(newVal),
    })
  }
})

const openAdvancedRagSettingsModal = () => {
  isAdvancedRagSettingsModalOpen.value = true
}

const descriptionOverLimit = computed(
  () => (modelValue.value.agent_description?.length ?? 0) > 1500,
)
</script>

<template>
  <div class="flex flex-col space-y-6">
    <OCardAdvanced
      elevation="S"
      border-radius="XL"
      content-class="border border-neutral-5"
      inline
      :title="t('general_information')"
      :subtitle="t('define_the_basic')"
    >
      <OInput
        v-model="modelValue.agent_name"
        required
        class="col-span-full"
        :label="t('agent_name')"
        :placeholder="t('agent_name.placeholder')"
        size="L"
        data-testid="agent-name-input"
        data-error-for="agent_name"
        :error-message="visibleErrors.general_information?.agent_name"
        :tooltipContent="t('agent_name.subtitle')"
      />
      <ONotification
        v-if="props.customSkillsEnabled"
        class="col-span-full w-full"
        type="notice"
        :title="t('main_skills.custom_skills_enabled_title')"
        :closable="false"
        data-testid="custom-skills-description-banner"
      >
        <template #description>
          <i18n-t keypath="main_skills.custom_skills_enabled_description" tag="span">
            <template #link>
              <button
                type="button"
                class="text-primary-info underline"
                data-testid="custom-skills-experimental-link-description"
                @click="emit('navigateToExperimental')"
              >
                {{ t('main_skills.experimental_tab') }}
              </button>
            </template>
          </i18n-t>
        </template>
      </ONotification>
      <ONotification
        v-if="descriptionOverLimit && !props.customSkillsEnabled"
        class="col-span-full w-full"
        type="warning"
        :title="t('agent_description.warning_title')"
        :description="t('agent_description.warning_description')"
        :closable="false"
        data-testid="agent-description-overlimit-warning"
      />
      <div class="col-span-full">
        <OTextArea
          v-show="props.customSkillsEnabled"
          :model-value="modelValue.agent_description"
          :label="t('description')"
          :placeholder="t('agent_description.placeholder')"
          data-testid="agent-description-input-disabled"
          data-error-for="agent_description"
          :tooltip-content="t('agent_description.tooltip')"
          disabled
        />
        <VariableMentionTextArea
          v-show="!props.customSkillsEnabled"
          v-model="modelValue.agent_description"
          :label="t('description')"
          :placeholder="t('agent_description.placeholder')"
          data-testid="agent-description-input"
          data-error-for="agent_description"
          :tooltip-content="t('agent_description.tooltip')"
          :max-length="1500"
        />
      </div>

      <div class="w-full border-t border-gray-200 my-4 col-span-2" />

      <div class="space-y-4 col-span-2">
        <div class="flex items-center justify-between gap-4">
          <OInputLabel
            :props="{
              label: t('supported_languages'),
              required: true,
              tooltipContent: t('choose_the_languages_this_agent_can_understand_and_speak'),
              inline: true,
            }"
          />
          <OSwitch
            v-model="showOnlySelected"
            :label="t('show_only_selected_language')"
            dataTestid="show-only-selected-toggle"
          />
        </div>

        <div class="flex flex-col w-full divide-y divide-gray-200">
          <div
            v-for="lang in filteredLanguageOptions"
            :key="lang.code"
            class="flex flex-wrap items-center gap-x-3 gap-y-2 py-3 w-full"
            :data-testid="`supported-language-row-${lang.code}`"
          >
            <OSwitch
              class="shrink-0 w-48"
              :model-value="isLanguageEnabled(lang.code)"
              @update:model-value="
                (val: boolean | undefined) => toggleLanguage(lang.code, val ?? false)
              "
              :label="lang.label"
              :dataTestid="`switch-${lang.code}`"
            >
              <template #titleExtra>
                <OBadge
                  class="ml-2"
                  v-if="hasBetaBadge(lang.code)"
                  color="notice"
                  :content="t('beta')"
                  variant="secondary"
                />
              </template>
            </OSwitch>

            <div class="flex items-center gap-2 w-full sm:flex-1">
              <ODropdown
                v-model="voiceIdByLang(lang.code).value"
                :options="getVoiceOptionsForLang(lang.code)"
                isLocaleDropdown
                medium
                :noResultsMessage="t('no_voice_found')"
                :searchable="getVoiceOptionsForLang(lang.code).length >= 6"
                :undefined-selected="t('choose_a_voice')"
                class="min-w-48 max-w-xs !outline-primary"
                dataTestid="voice-selection"
                :disabled="isLoadingVoices"
              />
              <OToolTip
                v-if="getSelectedVoiceSamplePath(lang.code)"
                :shadow-host-selector="WebComponentTags.AiVirtualAgentConfigurator"
                :content="t('selected_voice preview')"
                class="inline-flex"
              >
                <OAudioDemo
                  :key="getSelectedVoiceSamplePath(lang.code)"
                  :src="getSelectedVoiceSamplePath(lang.code)"
                />
              </OToolTip>
              <div class="ml-auto shrink-0">
                <OBtnChip
                  :title="t('default_language')"
                  :selected="defaultLanguageCode === lang.code"
                  neutral-unselected
                  :data-testid="`default-selection${lang.code}`"
                  @click="defaultLanguageCode = lang.code"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </OCardAdvanced>

    <FormSection
      :title="t('main_skills.title')"
      :subtitle="t('main_skills.description')"
      data-testid="main-skills-card"
    >
      <OSwitch
        v-model="qualifyCallsByIntent"
        :label="t('main_skills.qualify_calls_by_intent')"
        :tooltip-content="t('main_skills.qualify_calls_by_intent_tooltip')"
        dataTestid="main-skill-qualify-call"
      />

      <div class="flex items-center gap-l" data-testid="main-skill-answer-with-rag-row">
        <div class="min-w-0 flex-1">
          <OSwitch
            v-model="answerQuestionsWithDocumentation"
            :label="t('main_skills.answer_questions_with_documentation')"
            :tooltip-content="t('main_skills.answer_questions_with_documentation_tooltip')"
            dataTestid="main-skill-answer-with-rag"
          />
        </div>

        <OButton
          class="ml-auto shrink-0 font-(--typography-body-2-font-weight)!"
          :label="t('main_skills.advanced_settings')"
          variant="tertiary"
          size="S"
          :disabled="props.customSkillsEnabled || !answerQuestionsWithDocumentation"
          @click="openAdvancedRagSettingsModal"
          dataTestid="main-skills-advanced-settings"
        />
      </div>

      <ONotification
        v-if="hasMultipleMainSkills"
        type="notice"
        :title="t('main_skills.multiple_skills_combined_title')"
        :description="t('main_skills.multiple_skills_combined_description')"
        :closable="false"
        data-testid="main-skills-warning"
      />

      <AdvancedRagSettingsModal
        v-model:open="isAdvancedRagSettingsModalOpen"
        v-model="ragSettings"
      />

      <ODivider />

      <OSwitch
        v-model="stepValidation"
        :label="t('main_skills.step_validation')"
        :tooltip-content="t('main_skills.step_validation_tooltip')"
        dataTestid="main-skill-step-validation"
      />
    </FormSection>
  </div>
</template>
